/**
 * src/i18n.js
 *
 * src/i18n.js
 * 화면에 표현해야할 text 가 너무 많아서 다국어를 기능별로 분리 하기로 하였다.
 * 기존하 한개 파일로 쓰던거나,나누는 거나 로직적으로 큰 차이는 없고
 * const ko = { 선언해서 import 한 다국어 파일을 합쳐주면된다..
 *

 * 다국어 리소스를 기능별 JSON으로 분리하여 로딩/병합합니다.
 * - Vue 3 + vue-i18n v9 (Composition 모드, legacy:false)
 * - 운영(프로덕션)에서는 누락 키 로그 억제
 * - locale 변경 시 즉시 반영(.value) + <html lang> 동기화
 */

import { createI18n } from 'vue-i18n'
import { logWarn } from '@/utils/logHelper'

/* -------------------------------------------------------------------------- */
/* 1) Locale JSON Imports (기능별 분리)                                        */
/* -------------------------------------------------------------------------- */
/** EN */
import enCommon from '@/constants/locales/en/en.json'
import enAccount from '@/constants/locales/en/account.json'
import enMaterials from '@/constants/locales/en/materials.json'
import enRangeslider from '@/constants/locales/en/rangeslider.json'
import enProduction from '@/constants/locales/en/production.json'
import enMaterialCode from '@/constants/locales/en/materialCode.json'
import enOptions from '@/constants/locales/en/options.json'
import enRawMaterial from '@/constants/locales/en/rawMaterial.json'
import enMaterialsDescription from '@/constants/locales/en/materialsDescription.json'
import enMaterialsCautions from '@/constants/locales/en/materialsCautions.json'
import enIdeaSubmission from '@/constants/locales/en/quoteForm.json'
import enService from '@/constants/locales/en/service.json'
import enMachineMap from '@/constants/locales/en/MachineMap.json'
import enGoods from '@/constants/locales/en/goods.json'
import enService3dProcessDetail from '@/constants/locales/en/service3d_processDetail.json'
import enEquipmentDetail from '@/constants/locales/en/equipment_processDetail.json'

/** KO */
import koCommon from '@/constants/locales/ko/ko.json'
import koAccount from '@/constants/locales/ko/account.json'
import koMaterials from '@/constants/locales/ko/materials.json'
import koRangeslider from '@/constants/locales/ko/rangeslider.json'
import koProduction from '@/constants/locales/ko/production.json'
import koMaterialCode from '@/constants/locales/ko/materialCode.json'
import koOptions from '@/constants/locales/ko/options.json'
import koRawMaterial from '@/constants/locales/ko/rawMaterial.json'
import koMaterialsDescription from '@/constants/locales/ko/materialsDescription.json'
import koMaterialsCautions from '@/constants/locales/ko/materialsCautions.json'
import koIdeaSubmission from '@/constants/locales/ko/quoteForm.json'
import koService from '@/constants/locales/ko/service.json'
import koMachineMap from '@/constants/locales/ko/MachineMap.json'
import koGoods from '@/constants/locales/ko/goods.json'
import koService3dProcessDetail from '@/constants/locales/ko/service3d_processDetail.json'
import koEquipmentDetail from '@/constants/locales/ko/equipment_processDetail.json'


/** JA */
import jaCommon from '@/constants/locales/ja/ja.json'
import jaAccount from '@/constants/locales/ja/account.json'
import jaMaterials from '@/constants/locales/ja/materials.json'
import jaRangeslider from '@/constants/locales/ja/rangeslider.json'
import jaProduction from '@/constants/locales/ja/production.json'
import jaMaterialCode from '@/constants/locales/ja/materialCode.json'
import jaOptions from '@/constants/locales/ja/options.json'
import jaRawMaterial from '@/constants/locales/ja/rawMaterial.json'
import jaMaterialsDescription from '@/constants/locales/ja/materialsDescription.json'
import jaMaterialsCautions from '@/constants/locales/ja/materialsCautions.json'
import jaIdeaSubmission from '@/constants/locales/ja/quoteForm.json'
import jaService from '@/constants/locales/ja/service.json'
import jaMachineMap from '@/constants/locales/ja/MachineMap.json'
import jaGoods from '@/constants/locales/ja/goods.json'
import jaService3dProcessDetail from '@/constants/locales/ja/service3d_processDetail.json'
import jaEquipmentDetail from '@/constants/locales/ja/equipment_processDetail.json'

/* -------------------------------------------------------------------------- */
/* 2) Safe Deep Merge (동일 네임스페이스 키 충돌 시 하위 키를 병합)            */
/*    - 같은 네임스페이스(예: machineInfo)를 여러 파일에서 확장해도 안전하도록 */
/* -------------------------------------------------------------------------- */
function deepMerge(target = {}, src = {}) {
  for (const k of Object.keys(src)) {
    const v = src[k]
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      target[k] = deepMerge(target[k] || {}, v)
    } else {
      target[k] = v
    }
  }
  return target
}
function mergeAll(...objs) {
  return objs.reduce((acc, cur) => deepMerge(acc, cur), {})
}

/* -------------------------------------------------------------------------- */
/* 3) 언어별 메시지 병합                                                       */
/* -------------------------------------------------------------------------- */
const en = mergeAll(
  enCommon,
  enAccount,
  enMaterials,
  enRangeslider,
  enProduction,
  enMaterialCode,
  enOptions,
  enRawMaterial,
  enMaterialsDescription,
  enMaterialsCautions,
  enIdeaSubmission,
  enService,
  enService3dProcessDetail,
  enEquipmentDetail,
  enMachineMap,
  enGoods,
)

const ko = mergeAll(
  koCommon,
  koAccount,
  koMaterials,
  koRangeslider,
  koProduction,
  koMaterialCode,
  koOptions,
  koRawMaterial,
  koMaterialsDescription,
  koMaterialsCautions,
  koIdeaSubmission,
  koService,
  koService3dProcessDetail, 
  koEquipmentDetail,
  koMachineMap,
  koGoods,
)

const ja = mergeAll(
  jaCommon,
  jaAccount,
  jaMaterials,
  jaRangeslider,
  jaProduction,
  jaMaterialCode,
  jaOptions,
  jaRawMaterial,
  jaMaterialsDescription,
  jaMaterialsCautions,
  jaIdeaSubmission,
  jaService,
  jaService3dProcessDetail,
  jaEquipmentDetail,
  jaMachineMap,
  jaGoods,
)

/* -------------------------------------------------------------------------- */
/* 4) 초기 로케일 확정 + 유효성 보정                                           */
/* -------------------------------------------------------------------------- */
const SUPPORTED = ['ko', 'en', 'ja']
function normalizeLocale(lc) {
  if (!lc) return 'ko'
  const short = String(lc).split('-')[0].toLowerCase()
  return SUPPORTED.includes(short) ? short : 'ko'
}
const storedLang = normalizeLocale(localStorage.getItem('languageCode') || 'ko')

/* -------------------------------------------------------------------------- */
/* 5) i18n 인스턴스 생성                                                       */
/* -------------------------------------------------------------------------- */
const i18n = createI18n({
  /**
   * Composition API 모드 (권장)
   * - useI18n(), i18n.global.locale.value 등 사용
   */
  legacy: false,

  /**
   * 초기 로케일 (LocalStorage에서 복원)
   */
  locale: storedLang,

  /**
   * 폴백 로케일
   * - 현 로케일에 키 없을 때 순차 폴백 (KO → EN)
   */
  fallbackLocale: ['ko', 'en'],

  /**
   * 리소스 메시지
   */
  messages: { ko, en, ja },

  /**
   * 템플릿에서 $t() 바로 사용
   */
  globalInjection: true,

  /**
   * 누락/폴백 경고 끄고, 대신 커스텀 missing 핸들러로 제어
   */
  missingWarn: false,
  fallbackWarn: false,

  /**
   * 누락 키 로깅 (개발 환경에서만)
   */
  missing: (locale, key /*, instance */) => {
    if (process.env.NODE_ENV !== 'production') {
      logWarn(`[i18n missing] '${key}' @ ${locale}`)
    }
  },

  /**
   * 보안 옵션: 파라미터 HTML 이스케이프
   * - 번역 문자열 내 {param} 값에 잠재적 HTML이 들어오더라도 자동 이스케이프
   * - 템플릿에 의도적으로 HTML을 넣고 싶다면 v-html과 별도 키를 사용
   */
  escapeParameterHtml: true,
})

/* 최초 로드 시 <html lang> 동기화 */
document.documentElement.setAttribute('lang', storedLang)

/* -------------------------------------------------------------------------- */
/* 6) 언어 변경 헬퍼                                                            */
/*    - 전역 locale을 즉시 변경(.value)                                        */
/*    - LocalStorage 및 <html lang> 업데이트                                   */
/* -------------------------------------------------------------------------- */
export function changeLanguage(newLocale) {
  const norm = normalizeLocale(newLocale)
  i18n.global.locale.value = norm
  localStorage.setItem('languageCode', norm)
  document.documentElement.setAttribute('lang', norm)
}

export default i18n

/* -------------------------------------------------------------------------- */
/* (참고) Webpack 프로젝트에서 자동 로딩으로 바꾸고 싶다면:                    */
/*
  // 모든 JSON을 자동 병합 (ko/en/ja 각각 하위 모든 .json)
  function collect(ctx) {
    const out = {}
    ctx.keys().forEach((k) => deepMerge(out, ctx(k)?.default ?? ctx(k)))
    return out
  }
  const ko = collect(require.context('./constants/locales/ko', true, /\.json$/))
  const en = collect(require.context('./constants/locales/en', true, /\.json$/))
  const ja = collect(require.context('./constants/locales/ja', true, /\.json$/))
  const i18n = createI18n({ legacy:false, locale:storedLang, fallbackLocale:['ko','en'], messages:{ko,en,ja}, globalInjection:true })
*/
/* -------------------------------------------------------------------------- */

/*
글로벌 인스턴스 생성:
const i18n = createI18n(...)로 i18n 인스턴스를 생성한 뒤, app.use(i18n)를 통해 Vue 애플리케이션에 등록하면, 
전역적으로 $t() 함수를 사용할 수 있는 환경이 마련됩니다.

globalInjection 옵션:
globalInjection: true로 설정했기 때문에 템플릿 내에서 바로 {{ $t("key") }} 형태로 번역 문자열을 사용할 수 있습니다. 
이는 전역 i18n 인스턴스가 컴포넌트 템플릿에 주입되었기 때문입니다.

로케일 변경 시 전역 반영:
this.$i18n.locale = 'en' 또는 Composition API에서 locale.value = 'en'으로 언어를 변경하면, 
전역적으로 적용되어 모든 컴포넌트에서 $t()를 통해 표시되는 텍스트가 해당 언어로 즉시 반영됩니다.
*/

